// Internal modules
import type { CookieBannerQueryQuery } from '@/generated/graphql';
import { apolloClient } from '@/lib/apollo';
import { isProd } from '@/lib/utils/env';
import { COOKIE_BANNER_QUERY } from '../queries';

export const queryCookieBanner = (): Promise<
  CookieBannerQueryQuery['cookieBanner']
> => {
  /**
   * Why are we setting the `fetchPolicy` to `'network-only'`?
   *
   * The banner result depends on current cookies. If the user has accepted
   * the cookies before changing locales, maybe they just did it out of habit
   * to get rid of the thing, then we won't show the banner again, even though
   * the country/language is changing. Additionally, the content on the banner
   * can change based on banner policy (CCPA, GDPR, etc) and language.
   * Therefore, we don't ever want to rely on the Apollo cache but instead use
   * the `commonutils-client` backend module as the source of truth.
   */
  // @ts-expect-error the catch portion of this breaks the typing
  return apolloClient
    .query<CookieBannerQueryQuery>({
      context: {
        isBatchedQuery: true,
      },
      fetchPolicy: 'network-only',
      query: COOKIE_BANNER_QUERY,
    })
    .catch(err => {
      /**
       * The cookie banner service is fairly unreliable in non-production
       * environments and often returns { js: null, ... }, which causes an
       * error due to returning null from a non-nullable field. This block
       * of code is to prevent this error from causing a fallback since the
       * cookie banner isn't critical in non-production environments.
       */
      if (!isProd) {
        // eslint-disable-next-line no-console
        console.log(
          '%cIGNORING COOKIE BANNER ERROR IN NON-PRODUCTION ENV',
          'color: red',
        );

        return {
          data: {
            cookieBanner: {},
          },
        };
      }

      throw err;
    })
    .then(({ data }) => data.cookieBanner);
};
